/**
 * Images and PDFs a user attached to a chat message, after the message was
 * sent (user test fix, 2026-09-25: "When I refresh, the image I attached is
 * gone from the message").
 *
 * - The thread history lists them on each user message as `attachments`
 *   (`AgentMessageAttachment`), each with a 15-minute signed `url` at
 *   `GET /api/agent/attachments/:token` (an image inline, a PDF as a
 *   download). `historyMessageAttachments` turns them into assistant-ui
 *   attachments for `convertToThreadMessages` and remembers each link here.
 * - A link past `expiresAt`, or one that failed to load (403
 *   `ATTACHMENT_URL_EXPIRED`), asks for the history once more: the thread's
 *   refresher (registered by the chat provider) reads it and the fresh links
 *   replace the old ones. One read per link and at most one per thread every
 *   30 seconds, so a link that keeps failing never loops.
 * - A message just sent keeps showing its local preview: the chat runtime
 *   records what it sent, and a history read that has no attachments for
 *   that message (an older server, or a read that raced the save) shows the
 *   local preview until one has them.
 *
 * Module state like the draft store, shared by web and native. Nothing is
 * persisted; signed links never are.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';

export type AgentAttachmentEntry = {
  /** `<messageId>:<part>`, stable across reads. */
  id: string;
  threadId: string | null;
  type: 'image' | 'file';
  mimeType: string;
  name: string | null;
  bytes: number;
  width: number | null;
  height: number | null;
  url: string;
  /** Epoch milliseconds the link stops working. */
  expiresAt: number;
};

/** The shape assistant-ui takes on a `ThreadMessageLike` user message. */
export type MessageAttachmentLike = {
  id: string;
  type: 'image' | 'document';
  name: string;
  contentType: string;
  status: { type: 'complete' };
  content: (
    | { type: 'image'; image: string }
    | { type: 'file'; data: string; mimeType: string; filename?: string }
  )[];
};

export const AGENT_ATTACHMENT_LIMITS = {
  /** A link this close to its expiry counts as expired (clock drift). */
  expirySkewMs: 5_000,
  /** At most one history read per thread in this window. */
  refreshGapMs: 30_000,
  /** Local previews kept for messages just sent. */
  sentMax: 12,
  sentTtlMs: 30 * 60 * 1000,
} as const;

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function positive(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/** The attachments of one history message, validated; anything malformed is dropped. */
export function readAgentAttachments(value: unknown, threadId: string | null = null): AgentAttachmentEntry[] {
  if (!Array.isArray(value)) return [];
  const entries: AgentAttachmentEntry[] = [];
  for (const item of value) {
    const source = record(item);
    if (!source) continue;
    const { id, type, url, mimeType } = source;
    if (typeof id !== 'string' || !id) continue;
    if (type !== 'image' && type !== 'file') continue;
    if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) continue;
    const expiresAt = typeof source.expiresAt === 'string' ? Date.parse(source.expiresAt) : NaN;
    entries.push({
      id,
      threadId,
      type,
      mimeType: typeof mimeType === 'string' && mimeType ? mimeType : type === 'image' ? 'image/png' : 'application/pdf',
      name: typeof source.name === 'string' && source.name.trim() ? source.name.trim() : null,
      bytes: positive(source.bytes) ?? 0,
      width: positive(source.width),
      height: positive(source.height),
      url,
      expiresAt: Number.isFinite(expiresAt) ? expiresAt : Date.now() + 15 * 60 * 1000,
    });
  }
  return entries;
}

/** The assistant-ui attachment for one entry: an image part, or a file part for a PDF. */
export function toMessageAttachment(entry: AgentAttachmentEntry): MessageAttachmentLike {
  const name = entry.name ?? '';
  return entry.type === 'image'
    ? {
        id: entry.id,
        type: 'image',
        name,
        contentType: entry.mimeType,
        status: { type: 'complete' },
        content: [{ type: 'image', image: entry.url }],
      }
    : {
        id: entry.id,
        type: 'document',
        name,
        contentType: entry.mimeType,
        status: { type: 'complete' },
        content: [{ type: 'file', data: entry.url, mimeType: entry.mimeType, ...(name ? { filename: name } : {}) }],
      };
}

// ── Links, by attachment id ─────────────────────────────────────────────────

const links = new Map<string, AgentAttachmentEntry>();
const listeners = new Set<() => void>();

function notify() {
  for (const listener of [...listeners]) listener();
}

/** Remembers (or refreshes) the links of a history read. */
export function rememberAgentAttachments(entries: readonly AgentAttachmentEntry[]) {
  let changed = false;
  for (const entry of entries) {
    const current = links.get(entry.id);
    if (current && current.url === entry.url && current.expiresAt === entry.expiresAt) continue;
    links.set(entry.id, entry);
    changed = true;
  }
  if (changed) notify();
}

export function readAgentAttachment(id: string | null | undefined): AgentAttachmentEntry | null {
  return id ? links.get(id) ?? null : null;
}

export function subscribeAgentAttachments(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** One attachment's current link and facts; null for an attachment only the composer knows. */
export function useAgentAttachment(id: string | null | undefined): AgentAttachmentEntry | null {
  return useSyncExternalStore(subscribeAgentAttachments, () => readAgentAttachment(id), () => null);
}

/**
 * The link to show for an attachment: null while its link is expired (a
 * fresh one is being read), and `reportError` for an image that failed to
 * load (a 403 `ATTACHMENT_URL_EXPIRED`). `entry` is null for an attachment
 * only the composer knows (a message just sent): its local preview shows.
 */
export function useAgentAttachmentLink(id: string | null | undefined) {
  const entry = useAgentAttachment(id);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!entry) return;
    const wait = entry.expiresAt - AGENT_ATTACHMENT_LIMITS.expirySkewMs - Date.now();
    if (wait <= 0) {
      void requestFreshAgentAttachment(entry.id);
      return;
    }
    // Wake at the expiry so the link is read again before it fails.
    const timer = setTimeout(() => setNow(Date.now()), Math.min(wait, 2_147_000_000));
    return () => clearTimeout(timer);
  }, [entry, now]);
  const expired = entry ? isAgentAttachmentExpired(entry, now) : false;
  return {
    entry,
    url: entry && !expired ? entry.url : null,
    expired,
    reportError: () => {
      if (entry) void requestFreshAgentAttachment(entry.id);
    },
  };
}

export function isAgentAttachmentExpired(entry: Pick<AgentAttachmentEntry, 'expiresAt'>, now = Date.now()): boolean {
  return now >= entry.expiresAt - AGENT_ATTACHMENT_LIMITS.expirySkewMs;
}

// ── Fresh links: the history read once more ────────────────────────────────

type Refresher = () => Promise<{ messages: readonly unknown[] }>;
const refreshers = new Map<string, Refresher>();
const inflight = new Map<string, Promise<boolean>>();
const lastRead = new Map<string, number>();
const retriedUrls = new Set<string>();

/** The chat provider on screen tells the store how to read its thread's history. */
export function registerAgentAttachmentRefresher(threadId: string, refresher: Refresher) {
  refreshers.set(threadId, refresher);
  return () => {
    if (refreshers.get(threadId) === refresher) refreshers.delete(threadId);
  };
}

/** Reads the thread's history and keeps its attachments' fresh links. Resolves whether it read. */
export function refreshAgentAttachments(threadId: string, now = Date.now()): Promise<boolean> {
  const running = inflight.get(threadId);
  if (running) return running;
  const refresher = refreshers.get(threadId);
  const last = lastRead.get(threadId);
  if (!refresher || (last !== undefined && now - last < AGENT_ATTACHMENT_LIMITS.refreshGapMs)) {
    return Promise.resolve(false);
  }
  lastRead.set(threadId, now);
  const read = refresher()
    .then(({ messages }) => {
      for (const message of messages) {
        const source = record(message);
        if (source?.role !== 'user') continue;
        rememberAgentAttachments(readAgentAttachments(source.attachments, threadId));
      }
      return true;
    })
    .catch(() => false)
    .finally(() => inflight.delete(threadId));
  inflight.set(threadId, read);
  return read;
}

/**
 * An attachment's link expired or failed to load: read the history once for
 * that link. A link already retried is not asked for again.
 */
export function requestFreshAgentAttachment(id: string): Promise<boolean> {
  const entry = links.get(id);
  if (!entry?.threadId || retriedUrls.has(entry.url)) return Promise.resolve(false);
  retriedUrls.add(entry.url);
  return refreshAgentAttachments(entry.threadId);
}

// ── Messages just sent: their local previews ────────────────────────────────

type SentPreview = { attachments: readonly MessageAttachmentLike[]; at: number };
const sent = new Map<string, SentPreview>();

function sentKey(threadId: string, text: string) {
  return `${threadId}\n${text.replace(/\s+/g, ' ').trim()}`;
}

/** The chat runtime records what a message sent, once its thread is known. */
export function rememberSentAttachments(
  threadId: string | null | undefined,
  text: string,
  attachments: readonly MessageAttachmentLike[],
  now = Date.now(),
) {
  if (!threadId || attachments.length === 0) return;
  for (const [key, value] of sent) {
    if (now - value.at > AGENT_ATTACHMENT_LIMITS.sentTtlMs) sent.delete(key);
  }
  sent.set(sentKey(threadId, text), { attachments, at: now });
  while (sent.size > AGENT_ATTACHMENT_LIMITS.sentMax) {
    const oldest = sent.keys().next().value;
    if (oldest === undefined) break;
    sent.delete(oldest);
  }
}

/**
 * The attachments a history user message shows: the history's own (and the
 * local preview is forgotten), else the local preview of the message just
 * sent with the same text, else none.
 */
export function historyMessageAttachments(
  raw: unknown,
  threadId: string | null | undefined,
  text: string,
  now = Date.now(),
): MessageAttachmentLike[] {
  const entries = readAgentAttachments(raw, threadId ?? null);
  const key = threadId ? sentKey(threadId, text) : null;
  if (entries.length > 0) {
    rememberAgentAttachments(entries);
    if (key) sent.delete(key);
    return entries.map(toMessageAttachment);
  }
  if (!key) return [];
  const local = sent.get(key);
  if (!local || now - local.at > AGENT_ATTACHMENT_LIMITS.sentTtlMs) return [];
  return [...local.attachments];
}

/** The attachments a live message carries, in the shape the history gives back. */
export function sentMessageAttachments(value: unknown): MessageAttachmentLike[] {
  if (!Array.isArray(value)) return [];
  const out: MessageAttachmentLike[] = [];
  for (const item of value) {
    const source = record(item);
    if (!source || typeof source.id !== 'string' || !Array.isArray(source.content)) continue;
    const content: MessageAttachmentLike['content'] = [];
    for (const item of source.content as unknown[]) {
      const part = record(item);
      if (part?.type === 'image' && typeof part.image === 'string') {
        content.push({ type: 'image', image: part.image });
      } else if (part?.type === 'file' && typeof part.data === 'string') {
        content.push({ type: 'file', data: part.data, mimeType: typeof part.mimeType === 'string' ? part.mimeType : '' });
      }
    }
    if (content.length === 0) continue;
    out.push({
      id: source.id,
      type: source.type === 'image' ? 'image' : 'document',
      name: typeof source.name === 'string' ? source.name : '',
      contentType: typeof source.contentType === 'string' ? source.contentType : '',
      status: { type: 'complete' },
      content,
    });
  }
  return out;
}

// ── Display ────────────────────────────────────────────────────────────────

/**
 * The image's box inside the message's attachment block: its own ratio
 * from `width` and `height`, as large as the block allows, never upscaled.
 * Null without a known size (the block's own default applies).
 */
export function attachmentImageBox(
  entry: Pick<AgentAttachmentEntry, 'width' | 'height'> | null,
  max: { width: number; height: number },
): { width: number; height: number } | null {
  if (!entry?.width || !entry.height) return null;
  const scale = Math.min(1, max.width / entry.width, max.height / entry.height);
  return { width: Math.max(1, Math.round(entry.width * scale)), height: Math.max(1, Math.round(entry.height * scale)) };
}

/** "120 kB", "1.2 MB" in the reader's locale; empty for an unknown size. */
export function formatAttachmentBytes(bytes: number, locale: string): string {
  if (!bytes || bytes <= 0) return '';
  const megabytes = bytes >= 1_000_000;
  const value = megabytes ? bytes / 1_000_000 : Math.max(1, bytes / 1_000);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: megabytes ? 'megabyte' : 'kilobyte',
      unitDisplay: 'short',
      maximumFractionDigits: megabytes ? 1 : 0,
    }).format(value);
  } catch {
    return `${megabytes ? value.toFixed(1) : Math.round(value)} ${megabytes ? 'MB' : 'kB'}`;
  }
}

export function resetAgentAttachmentsForTests() {
  links.clear();
  listeners.clear();
  refreshers.clear();
  inflight.clear();
  lastRead.clear();
  retriedUrls.clear();
  sent.clear();
}
